import { createSlice } from '@reduxjs/toolkit';
import {
  cancelWmsOrder,
  cancelWmsOrdersBulk,
  fetchWmsOrder,
  fetchWmsOrders,
  reserveWmsOrder,
  reserveWmsOrdersBulk,
} from './wmsOrdersThunks';
import { ALL_FILTER } from './wmsOrdersUtils';
import { selectWarehouse } from '../warehouseContext/warehouseContextSlice';

const initialState = {
  /** Current page of orders, each with its warehouse state already derived. */
  orders: [],
  totalCount: 0,
  /** How many orders there are in each state, over the other filters. */
  stateCounts: null,
  /** The date the server looked back to, when no date was asked for. */
  windowFrom: null,
  /** Orders in other warehouses than the one in use, when there are any. */
  elsewhere: null,
  loading: false,
  error: null,
  /** The request whose answer may still write. See `fetchWmsOrders.fulfilled`. */
  requestId: null,
  filters: {
    status: ALL_FILTER,
    company: ALL_FILTER,
    source: ALL_FILTER,
    search: '',
    dateFrom: '',
    dateTo: '',
    page: 1,
    rowsPerPage: 20,
  },

  /** The order open on its own page. */
  detail: null,
  detailLoading: false,
  detailError: null,

  /** The order being cancelled, to disable only its controls. */
  cancellingId: null,
  /** The order being reserved, same idea. */
  reservingId: null,
  /** Which bulk is running, if any: 'reserve' | 'cancel'. */
  bulkBusy: null,
};

const wmsOrdersSlice = createSlice({
  name: 'wmsOrders',
  initialState,
  reducers: {
    /** Merges a patch into the filters; anything but a page change goes back to page 1. */
    setWmsOrderFilters: (state, action) => {
      state.filters = { ...state.filters, page: 1, ...action.payload };
    },
    /** Leaving the detail: the next order must not open on this one. */
    clearWmsOrderDetail: (state) => {
      state.detail = null;
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWmsOrders.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchWmsOrders.fulfilled, (state, action) => {
        // Only the latest request may write: a cancellation reloads the
        // listing while a filter may still be on its way.
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.orders = action.payload.orders || [];
        state.totalCount = action.payload.count || 0;
        state.stateCounts = action.payload.state_counts || null;
        state.windowFrom = action.payload.window_from || null;
        state.elsewhere = action.payload.elsewhere || null;
      })
      .addCase(fetchWmsOrders.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.error = action.payload;
        state.orders = [];
        state.totalCount = 0;
        state.elsewhere = null;
      })

      .addCase(fetchWmsOrder.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchWmsOrder.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.detail = action.payload;
      })
      .addCase(fetchWmsOrder.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload;
      })

      .addCase(cancelWmsOrder.pending, (state, action) => {
        state.cancellingId = action.meta.arg;
      })
      .addCase(cancelWmsOrder.fulfilled, (state, action) => {
        state.cancellingId = null;
        // The open order takes the row the server answered: its state, its
        // reservations gone. The lines and the history are asked again by
        // whoever has the page open.
        if (state.detail?.id === action.payload.order.id) {
          state.detail = { ...state.detail, ...action.payload.order };
        }
      })
      // Not stored as `error`: the dialog that asked shows it.
      .addCase(cancelWmsOrder.rejected, (state) => {
        state.cancellingId = null;
      })

      .addCase(reserveWmsOrder.pending, (state, action) => {
        state.reservingId = action.meta.arg;
      })
      .addCase(reserveWmsOrder.fulfilled, (state, action) => {
        state.reservingId = null;
        // The row comes back with its new state; the figures of the reserve
        // itself are for the notice, not for the order.
        const { reserved_now, lines_complete, lines_total, ...row } = action.payload;
        if (state.detail?.id === row.id) {
          state.detail = { ...state.detail, ...row };
        }
      })
      .addCase(reserveWmsOrder.rejected, (state) => {
        state.reservingId = null;
      })

      .addCase(reserveWmsOrdersBulk.pending, (state) => {
        state.bulkBusy = 'reserve';
      })
      .addCase(reserveWmsOrdersBulk.fulfilled, (state) => {
        state.bulkBusy = null;
      })
      .addCase(reserveWmsOrdersBulk.rejected, (state) => {
        state.bulkBusy = null;
      })
      .addCase(cancelWmsOrdersBulk.pending, (state) => {
        state.bulkBusy = 'cancel';
      })
      .addCase(cancelWmsOrdersBulk.fulfilled, (state) => {
        state.bulkBusy = null;
      })
      .addCase(cancelWmsOrdersBulk.rejected, (state) => {
        state.bulkBusy = null;
      })

      // Another warehouse is another set of clients, and another "elsewhere".
      .addCase(selectWarehouse, (state) => {
        state.filters.company = ALL_FILTER;
        state.filters.page = 1;
      });
  },
});

export const { setWmsOrderFilters, clearWmsOrderDetail } = wmsOrdersSlice.actions;

export default wmsOrdersSlice.reducer;
